import { useState } from 'react';
import { Tab } from '@headlessui/react';
import {
  ChatBubbleLeftRightIcon,
  MegaphoneIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

export default function Communications() {
  const [categories] = useState([
    {
      id: 'internal',
      name: 'Internal Communications',
      icon: ChatBubbleLeftRightIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-4">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Internal Communication Channels</h3>
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Secure Messaging Platform</h4>
                  <p className="text-sm text-gray-600 mt-1">Real-time secure communication for team members</p>
                  <button className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Open Chat
                  </button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Emergency Notification System</h4>
                  <p className="text-sm text-gray-600 mt-1">Broadcast critical updates to all team members</p>
                  <button className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Send Alert
                  </button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Team Briefings</h4>
                  <p className="text-sm text-gray-600 mt-1">Schedule and manage team update meetings</p>
                  <button className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Schedule Briefing
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'external',
      name: 'External Communications',
      icon: MegaphoneIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-4">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">External Communication Channels</h3>
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Media Statements</h4>
                  <p className="text-sm text-gray-600 mt-1">Prepare and distribute press releases</p>
                  <button className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Draft Statement
                  </button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Customer Notifications</h4>
                  <p className="text-sm text-gray-600 mt-1">Send updates to affected customers</p>
                  <button className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Create Notice
                  </button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Regulatory Reports</h4>
                  <p className="text-sm text-gray-600 mt-1">Submit required regulatory filings</p>
                  <button className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    File Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'templates',
      name: 'Communication Templates',
      icon: DocumentTextIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-4">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Pre-approved Templates</h3>
              <div className="mt-4 grid grid-cols-1 gap-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium">Initial Incident Notification</h4>
                  <p className="text-sm text-gray-600 mt-1">Template for first communication about an incident</p>
                  <div className="mt-2 flex space-x-2">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                      Use Template
                    </button>
                    <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                      Preview
                    </button>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium">Status Update</h4>
                  <p className="text-sm text-gray-600 mt-1">Template for ongoing incident updates</p>
                  <div className="mt-2 flex space-x-2">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                      Use Template
                    </button>
                    <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                      Preview
                    </button>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium">Resolution Notice</h4>
                  <p className="text-sm text-gray-600 mt-1">Template for incident closure communication</p>
                  <div className="mt-2 flex space-x-2">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                      Use Template
                    </button>
                    <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                      Preview
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Communications</h2>
      <Tab.Group>
        <Tab.List className="flex space-x-4 border-b border-gray-200">
          {categories.map((category) => (
            <Tab
              key={category.id}
              className={({ selected }) =>
                clsx(
                  'flex items-center space-x-2 py-2 px-4 text-sm font-medium border-b-2 outline-none',
                  selected
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )
              }
            >
              <category.icon className="h-5 w-5" />
              <span>{category.name}</span>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {categories.map((category) => (
            <Tab.Panel key={category.id}>{category.content}</Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}